import { Tag } from '@carbon/react';

export type ProvenanceState = 'ai' | 'user' | 'corrected';

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

interface ProvenanceSectionProps {
  provenance: ProvenanceState | undefined;
  createdByName: string | null | undefined;
  updatedByName: string | null | undefined;
  correctedAt: string | null | undefined;
  testId?: string;
}

/**
 * Read-only origin/author section shared by NodeDetailsPanel and
 * EdgeDetailsPanel.  Shows the provenance state tag plus author and
 * correction-date lines where applicable.
 */
export function ProvenanceSection({
  provenance,
  createdByName,
  updatedByName,
  correctedAt,
  testId = 'provenance',
}: ProvenanceSectionProps) {
  const state = provenance ?? 'ai';
  const stateLabel =
    state === 'user' ? 'Added by you' :
    state === 'corrected' ? 'AI-generated · Corrected' :
    'AI-generated';
  const tagType: 'blue' | 'green' | 'warm-gray' =
    state === 'user' ? 'green' :
    state === 'corrected' ? 'warm-gray' :
    'blue';

  return (
    <div
      data-testid={testId}
      style={{
        padding: '0.5rem 1rem',
        borderBottom: '1px solid var(--cds-border-subtle-01)',
      }}
    >
      <p
        style={{
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.02em',
          color: 'var(--cds-text-secondary)',
          marginBottom: '0.25rem',
        }}
      >
        Origin
      </p>
      <Tag type={tagType} size="sm">{stateLabel}</Tag>
      <div style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', marginTop: '0.25rem' }}>
        {createdByName && <div>Created by {createdByName}</div>}
        {state === 'corrected' && updatedByName && <div>Corrected by {updatedByName}</div>}
        {state === 'corrected' && correctedAt && <div>Corrected on {formatDate(correctedAt)}</div>}
        {state === 'user' && updatedByName && updatedByName !== createdByName && (
          <div>Last edited by {updatedByName}</div>
        )}
      </div>
    </div>
  );
}

// Made with Bob
